import { Link } from "react-router-dom";
import { useTokens } from "@/design-system/hooks/useTokens";
import {
  ThemeToggleButton,
  LanguageToggleButton,
} from "@/ui/components/common";
import { useMenuService } from "@/application/services/menuService";
import { useSidebar } from "@/application/contexts/SidebarContext";
import { NavLinks } from "./NavLinks";
import { UserMenu } from "./UserMenu";
import { MobileMenu } from "./MobileMenu";
import { CubeIcon } from "@heroicons/react/24/outline";
import { MobileMenuButton } from "./MobileMenuButton";
import { ProjectSelector } from "./ProjectSelector";
import { useEffect } from "react";
// 確保 ProjectSelector 組件已經按照 DDD 架構重構

/**
 * 導航欄組件
 * 提供應用程式的主要導航功能
 */
export function Navbar() {
  const tokens = useTokens();
  const { isMenuOpen, toggleMenu } = useMenuService();
  const { isOpen, toggleSidebar, mode } = useSidebar();

  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      if (event.reason?.config?.url?.includes("/users/me")) {
        console.error("未處理的用戶API錯誤:", {
          status: event.reason.response?.status,
          config: event.reason.config,
        });
        window.dispatchEvent(
          new CustomEvent("api-error", {
            detail: {
              type: "user-api-failure",
              error: event.reason,
            },
          }),
        );
      }
    };
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);

  return (
    <nav
      className="bg-white dark:bg-gray-800 shadow-sm fixed top-0 left-0 right-0 z-10"
      style={{
        backgroundColor: tokens.colors.background.paper,
        boxShadow: tokens.shadows.sm,
      }}
    >
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* 左側導航 */}
          <div className="flex items-center">
            {/* 側邊欄切換按鈕 - 只在行動版顯示 */}
            {mode === "overlay" && (
              <button
                onClick={toggleSidebar}
                className="mr-4 p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                aria-label={!isOpen ? "展開側邊欄" : "收起側邊欄"}
              >
                <CubeIcon className="w-5 h-5" />
              </button>
            )}

            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex items-center mr-4">
              <span className="text-2xl font-semibold text-primary-500">
                麻薏 MayiAI
              </span>
            </Link>

            {/* 專案選擇器 */}
            <ProjectSelector />

            {/* 標準導航項目 - 顯示子導航 */}
            <div className="hidden sm:flex sm:space-x-4 ml-4">
              <NavLinks showMainNav={false} />
            </div>
          </div>

          {/* 右側功能 */}
          <div className="hidden sm:ml-6 sm:flex sm:items-center sm:space-x-4">
            {/* 主題切換 */}
            <ThemeToggleButton />
            <LanguageToggleButton />

            {/* 用戶選單 */}
            <UserMenu />
          </div>

          {/* 移動端選單按鈕 */}
          <div className="flex items-center sm:hidden">
            <MobileMenuButton isOpen={isMenuOpen} onClick={toggleMenu} />
          </div>
        </div>
      </div>

      {/* 移動端選單 */}
      <MobileMenu isOpen={isMenuOpen} onClose={toggleMenu} />
    </nav>
  );
}
